import { useCallback, useEffect, useRef } from 'react';
import { PoolNotification } from '@/types/notification';
import { Button } from '../ui/Button';
import { ContentWithLoading } from '../ui/ContentWithLoading';
import NotificationCard from './NotificationCard';

type NotificationDropdownContentProps = {
  notifications: PoolNotification[];
  loading: boolean;
  onItemClick: (poolId: number) => void;
  onMarkAllAsRead: () => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
};

export function NotificationDropdownContent({
  notifications,
  loading,
  onItemClick,
  onMarkAllAsRead,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: NotificationDropdownContentProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasMore && !isLoadingMore) {
        onLoadMore();
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(handleIntersect, {
      root,
      rootMargin: '0px 0px 100px 0px',
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleIntersect]);

  return (
    <div className="bg-[#F4F4F4] rounded-3xl py-6 flex flex-col gap-3.5 shadow-lg max-h-[400px]">
      <h2 className="text-black text-base font-medium px-6">Notifications</h2>

      <div
        ref={scrollContainerRef}
        className="flex flex-col gap-3.5 overflow-y-auto min-h-0 h-full px-6 notification-scrollbar"
      >
        <ContentWithLoading
          loading={loading}
          empty={!loading && notifications.length === 0}
          emptyTitle="No notifications yet"
          emptySubtitle="You don't have any notifications yet."
        >
          {notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              variant="compact"
              onClick={onItemClick}
            />
          ))}
          <div ref={sentinelRef} className="shrink-0">
            {isLoadingMore && <div className="text-center py-3 text-[#B2B2B2] text-sm">Loading more...</div>}
          </div>
        </ContentWithLoading>
      </div>

      {notifications.length > 0 && (
        <div className="w-full flex justify-center items-center px-6">
          <Button
            variant="text"
            onClick={onMarkAllAsRead}
            className="text-[#767676] text-sm font-medium underline cursor-pointer"
          >
            Mark all as read
          </Button>
        </div>
      )}
    </div>
  );
}
